import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TInputAppState } from '../reducer';
import {
  TBctxItemWithoutSize,
  TInProgressDetails,
  TRESPONSE_RoamingQcTransactions,
  TRoamingQcEventWithoutStatus,
  TRoamingQcStoreLockedBy,
  TRoamingQcStoreLockResult,
  TTransactionAuditDTOModel,
  TTransactionGroupedAudits,
} from '@monorepo/types';
import { from, Observable, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import produce from 'immer';
import {
  getConfigurations,
  getBctxItemsOrderWithoutSize,
  getRqcTransactionsViewDescriptor,
} from '../app-master-db/app-master.query';
import {
  getCurrentRoundInfo,
  getEpfDetails,
  getInitialRoundInfo,
  getInitialRoundInfoAndInProgressDetails,
  getInProgressDetails,
  getRoamingQcLockDetailsAndNextCacheResetTimestamp,
  getRecentCategoryList,
  getTransactionLogRecords,
  getTransactionsPayloadMetadata,
  getAllStationIdsAndShifts,
} from './roaming-qc-db.query';
import {
  AcquireLockAndClearInProgressDetails,
  AcquireLockAndClearTransactionLogRecords,
  AcquireLockAndFetchTransactionLogRecords,
  AcquireLockAndThenInitStore,
  AcquireLockAndUpdateEpfDetailsAndRecentCategoryList,
  AcquireLockAndUpdateInProgressDetails,
  UpdateRoamingQcStoreLockDetails,
} from './roaming-qc-db.actions';
import * as _ from 'lodash';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { BCTXComputationHelper, RoamingQcUtil } from '@monorepo/utils';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import * as moment from 'moment';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BucketStorageService } from '@monorepo/services';

@Injectable({
  providedIn: 'root',
})
export class RoamingQcStoreFacade {
  private goToInspectionSubscription = new Subject<boolean>();

  constructor(
    private store: Store<TInputAppState>,
    private persistentStorageService: PersistentStorageService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private alertCtrl: AlertController,
    private translateService: TranslateService,
    private bucketStorageService: BucketStorageService,
  ) {}

  getGoToInspectionSubscription() {
    return this.goToInspectionSubscription.asObservable();
  }

  emitGoToInspectionSubscription(defaultToEmptyTransactionsInCaseOfError: boolean) {
    this.goToInspectionSubscription.next(defaultToEmptyTransactionsInCaseOfError);
  }

  getTransactionLogRecords() {
    return this.store.pipe(select(getTransactionLogRecords));
  }

  getConfigurations() {
    return this.store.pipe(select(getConfigurations));
  }

  getBctxItemsOrderWithoutSize() {
    return this.store.pipe(select(getBctxItemsOrderWithoutSize));
  }

  getRoamingQcLockDetailsAndNextCacheResetTimestamp() {
    return this.store.pipe(select(getRoamingQcLockDetailsAndNextCacheResetTimestamp));
  }

  getEpfDetails() {
    return this.store.pipe(select(getEpfDetails));
  }

  fetchTransactions(start: number, end: number) {
    this.store.dispatch(new AcquireLockAndFetchTransactionLogRecords(start, end));
  }

  getInProgressDetails() {
    return this.store.pipe(select(getInProgressDetails));
  }

  getCurrentRoundInfo() {
    return this.store.pipe(select(getCurrentRoundInfo));
  }

  getInitialRoundInfo() {
    return this.store.pipe(select(getInitialRoundInfo));
  }

  getInitialRoundInfoAndInProgressDetails() {
    return this.store.pipe(select(getInitialRoundInfoAndInProgressDetails));
  }

  fetchTransactionsForInitialRoundInfo(defaultToEmptyTransactionsInCaseOfError: boolean) {
    this.store.dispatch(
      new AcquireLockAndFetchTransactionLogRecords(new Date().getTime(), new Date().getTime(), {
        defaultToEmptyTransactionsInCaseOfError,
      }),
    );
  }

  initStore(initBy: TRoamingQcStoreLockedBy) {
    this.store.dispatch(new AcquireLockAndThenInitStore(initBy));
  }

  updateInProgressDetails(inProgressDetails: TInProgressDetails, toBeClosedModals: number) {
    this.store.dispatch(new AcquireLockAndUpdateInProgressDetails(inProgressDetails, toBeClosedModals));
  }

  clearInProgressDetails(toBeClosedModals: number) {
    this.store.dispatch(new AcquireLockAndClearInProgressDetails(toBeClosedModals));
  }

  saveInProgressDetails(inProgressDetails: TInProgressDetails, toBeClosedModals: number) {
    this.store.dispatch(new AcquireLockAndUpdateInProgressDetails(inProgressDetails, toBeClosedModals));
  }

  getRecentCategoryList() {
    return this.store.pipe(select(getRecentCategoryList));
  }

  clearTransactionLogRecordsAndCloseModal() {
    this.store.dispatch(new AcquireLockAndClearTransactionLogRecords());
  }

  getRecentCategoryListByBctx(bctxItemListWithoutSize: TBctxItemWithoutSize[]) {
    return this.getBctxItemsOrderWithoutSize().pipe(
      take(1),
      switchMap((res) => {
        return this.getRecentCategoryList().pipe(
          take(1),
          map((recentList) => {
            const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(res, bctxItemListWithoutSize);
            return recentList[bctxKey] ? recentList[bctxKey] : [];
          }),
        );
      }),
    );
  }

  acquireLock(lockFor: TRoamingQcStoreLockedBy): Observable<TRoamingQcStoreLockResult> {
    return this.getRoamingQcLockDetailsAndNextCacheResetTimestamp().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translateService.instant('roaming_qc_common.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translateService.instant('roaming_qc_common.factoryEndTimeWaitTillReset'),
          };
        } else {
          this.store.dispatch(
            new UpdateRoamingQcStoreLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  releaseLock() {
    return this.getRoamingQcLockDetailsAndNextCacheResetTimestamp()
      .pipe(take(1))
      .subscribe(({ lockDetails }) => {
        this.store.dispatch(
          new UpdateRoamingQcStoreLockDetails(
            produce(lockDetails, (draft) => {
              draft.isLocked = false;
              draft.lockedBy = 'NONE';
            }),
          ),
        );
      });
  }

  acquireLockAndUpdateEpfDetailsAndRecentCategoryList(
    bulkEvent: TRoamingQcEventWithoutStatus,
    toBeClosedModals: number,
  ) {
    this.store.dispatch(new AcquireLockAndUpdateEpfDetailsAndRecentCategoryList(bulkEvent, toBeClosedModals));
  }

  clearInProgressDetailsInCache() {
    return this.inputAppClientDBFacade.getSelectedStationIdAndShiftId().pipe(
      take(1),
      switchMap(({ shiftId, stationId }) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_ROAMING_QC_DB]')).pipe(
          take(1),
          switchMap((currentDB) => {
            const updatedDB = RoamingQcUtil.clearInProgressDetails(currentDB, stationId, shiftId);
            return from(this.persistentStorageService.set('[APP][INPUT_APP_ROAMING_QC_DB]', updatedDB)).pipe(
              take(1),
              map(() => {
                return updatedDB;
              }),
            );
          }),
        );
      }),
    );
  }

  updateInProgressDetailsInCache(inProgressDetails: TInProgressDetails) {
    return this.inputAppClientDBFacade.getSelectedStationIdAndShiftId().pipe(
      take(1),
      switchMap(({ shiftId, stationId }) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_ROAMING_QC_DB]')).pipe(
          take(1),
          switchMap((currentDB) => {
            const updatedDB = RoamingQcUtil.updateInProgressDetails(currentDB, stationId, shiftId, inProgressDetails);
            return from(this.persistentStorageService.set('[APP][INPUT_APP_ROAMING_QC_DB]', updatedDB)).pipe(
              take(1),
              map(() => {
                return updatedDB;
              }),
            );
          }),
        );
      }),
    );
  }

  getTransactionsPayloadMetadata() {
    return this.store.pipe(select(getTransactionsPayloadMetadata));
  }

  getTransactionsPayload(start: number, end: number) {
    return this.getTransactionsPayloadMetadata().pipe(
      take(1),
      map((metadata) => {
        const bufferTime = 86400000;
        let runningStart = new Date(start).setHours(0, 0, 0, 0) - bufferTime; // adding bufferTime
        const newEnd = new Date(end).setHours(23, 59, 59, 999) + bufferTime; // adding bufferTime
        const dates: string[] = [];
        while (runningStart <= newEnd) {
          dates.push(moment(runningStart).format('YYYY-MM-DD'));
          runningStart = runningStart + 86400000;
        }
        return {
          ...metadata,
          dates,
          window:
            moment
              .utc(start - bufferTime)
              .hours(0)
              .minutes(0)
              .seconds(0)
              .milliseconds(0)
              .toISOString() +
            '/' +
            moment
              .utc(end + bufferTime)
              .hours(23)
              .minutes(59)
              .seconds(59)
              .milliseconds(999)
              .toISOString(),
        };
      }),
    );
  }

  transformTransactionRecords(response: TRESPONSE_RoamingQcTransactions): TTransactionGroupedAudits[] {
    const records: TTransactionAuditDTOModel[] = response.map((transaction) => {
      return {
        audit_id: transaction.id,
        shift: transaction.shift,
        bctxItems: transaction.ctx,
        cycle_id: transaction.cycle_id,
        round: transaction.round,
        operator: transaction.operator,
        operation: transaction.operation.name,
        samples: transaction.samples,
        grade: transaction.status,
        datetime: transaction.dateTime,
        subject: transaction.subject,
        record_category: transaction.record_category,
      };
    });
    const groupedRecords: TTransactionGroupedAudits[] = Object.entries(
      _.groupBy(records, (record) => {
        return record.cycle_id;
      }),
    ).map(([key, records]) => {
      return {
        audits: records.sort((a, b) => (a.datetime > b.datetime ? -1 : 1)),
      };
    });
    groupedRecords.sort((a, b) => {
      return a.audits[0].datetime > b.audits[0].datetime ? -1 : 1;
    });
    return groupedRecords;
  }

  filterTransactionForDateRange(start: number, end: number, transactionRecords: TRESPONSE_RoamingQcTransactions) {
    return transactionRecords.filter((txn) => {
      const startDate = new Date(start).setHours(0, 0, 0, 0);
      const endDate = new Date(end).setHours(23, 59, 59, 999);
      return txn.dateTime >= startDate && txn.dateTime <= endDate;
    });
  }

  getRqcTransactionsViewDescriptor() {
    return this.store.pipe(select(getRqcTransactionsViewDescriptor));
  }

  isRoamingQcStoreUnlocked() {
    return this.getRoamingQcLockDetailsAndNextCacheResetTimestamp().pipe(
      take(1),
      map(({ lockDetails }) => !lockDetails.isLocked),
    );
  }

  updateIntitialRoundInfoInCache(round: number, station: string, shift: string) {
    return from(this.persistentStorageService.get('[APP][INPUT_APP_ROAMING_QC_DB]')).pipe(
      take(1),
      switchMap((currentDB) => {
        const updatedDB = RoamingQcUtil.updateInitialRoundDetails(round, currentDB, station, shift);
        return from(this.persistentStorageService.set('[APP][INPUT_APP_ROAMING_QC_DB]', updatedDB)).pipe(
          take(1),
          map(() => updatedDB),
        );
      }),
    );
  }

  async showErrorForRoundInit() {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('common.error'),
      message: this.translateService.instant('roaming_qc_common.notAbleToFetchRound'),
      backdropDismiss: false,
      cssClass: 'rqc-initial-round-error',
      buttons: [
        {
          text: this.translateService.instant('roaming_qc_common.insecptAsFirstRound'),
          handler: () => {
            this.emitGoToInspectionSubscription(true);
          },
        },
        {
          text: this.translateService.instant('roaming_qc_common.retry'),
          handler: () => {
            this.emitGoToInspectionSubscription(false);
          },
        },
      ],
    });
    alert.present();
  }

  getAllStationIdsAndShifts() {
    return this.store.pipe(select(getAllStationIdsAndShifts));
  }
}
